/**
 * MarketStatusRail — compact stacked status block designed for the fixed
 * left rail (≤88px wide) introduced by R78.
 *
 * Replaces the wide horizontal Market Status header that previously sat in
 * the Dashboard flow. Shows:
 *   - Market open/closed state for NSE, CDS, MCX, Crypto
 *   - On/Off state for the three user switches (Auto, Speech, Telegram)
 *
 * Each row is rendered as a small two-column chip:
 *   [ NSE  ]  ●
 *   [ CDS  ]  ●
 * Tooltip explains the meaning on hover.
 */
import React, { useEffect, useState } from 'react';
import { CH_TYPE } from '../../styles/appUi';
import { Box, Tooltip, Typography } from '@mui/material';
import { useAppTheme } from '../../hooks/useAppTheme';
import api from '../../services/api';

type ConnState = 'ok' | 'off' | 'unknown';

interface MarketStatusRailProps {
  autoTradeEnabled?: boolean;
  speechAlertEnabled?: boolean;
  telegramNotificationEnabled?: boolean;
}

const Row: React.FC<{
  label: string;
  on: boolean;
  C: any;
  tooltip: string;
}> = ({ label, on, C, tooltip }) => (
  <Tooltip arrow placement="right" title={tooltip}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 0.4,
        px: 0.5,
        py: 0.15,
        borderRadius: 0.5,
        bgcolor: on ? C.bullMuted : C.bearMuted,
        border: '1px solid',
        borderColor: on ? C.bullBorder : C.bearBorder,
      }}
    >
      <Typography
        sx={{
          fontSize: CH_TYPE.micro,
          fontWeight: 700,
          color: on ? C.bull : C.bear,
          letterSpacing: 0.3,
          textTransform: 'uppercase',
          lineHeight: 1,
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          bgcolor: on ? C.bull : C.bear,
          flexShrink: 0,
        }}
      />
    </Box>
  </Tooltip>
);

/** Three-state connection row (ok = green, off = red, unknown = grey). */
const ConnRow: React.FC<{ label: string; state: ConnState; C: any; tooltip: string }> = ({ label, state, C, tooltip }) => {
  const col = state === 'ok' ? C.bull : state === 'off' ? C.bear : C.neutral;
  const bg = state === 'ok' ? C.bullMuted : state === 'off' ? C.bearMuted : 'transparent';
  const border = state === 'ok' ? C.bullBorder : state === 'off' ? C.bearBorder : 'divider';
  return (
    <Tooltip arrow placement="right" title={tooltip}>
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.4,
        px: 0.5, py: 0.15, borderRadius: 0.5, bgcolor: bg, border: '1px solid', borderColor: border,
      }}>
        <Typography sx={{ fontSize: CH_TYPE.micro, fontWeight: 700, color: col, letterSpacing: 0.3, textTransform: 'uppercase', lineHeight: 1 }}>{label}</Typography>
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: col, flexShrink: 0,
          animation: state === 'off' ? 'pulseRed 1.2s infinite' : 'none' }} />
      </Box>
    </Tooltip>
  );
};

/** Poll broker + app reachability (mirrors the former sticky-header dots). */
function useConnectionStatus() {
  const [s, setS] = useState<{ zerodha: ConnState; delta: ConnState; app: ConnState }>({ zerodha: 'unknown', delta: 'unknown', app: 'unknown' });
  useEffect(() => {
    let cancelled = false;
    const readBroker = (rv: any): ConnState => {
      const d = rv?.data?.data || rv?.data || {};
      if (d.connected) return 'ok';
      if (d.hasCredentials) return 'off';
      return 'unknown';
    };
    const load = async () => {
      const [zr, dr] = await Promise.allSettled([
        api.get<any>('/broker/zerodha/status', { headers: { _suppressToast: 'true' } }),
        api.get<any>('/broker/delta/status', { headers: { _suppressToast: 'true' } }),
      ]);
      if (cancelled) return;
      const anyOk = zr.status === 'fulfilled' || dr.status === 'fulfilled';
      setS({
        zerodha: zr.status === 'fulfilled' ? readBroker(zr.value) : 'unknown',
        delta: dr.status === 'fulfilled' ? readBroker(dr.value) : 'unknown',
        app: anyOk ? 'ok' : 'unknown',
      });
    };
    load();
    const id = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);
  return s;
}

const SectionLabel: React.FC<{ text: string }> = ({ text }) => (
  <Typography
    variant="caption"
    sx={{
      fontWeight: 700,
      color: 'text.secondary',
      textTransform: 'uppercase',
      letterSpacing: 0.4,
      fontSize: CH_TYPE.micro,
      textAlign: 'center',
      lineHeight: 1,
      py: 0.15,
    }}
  >
    {text}
  </Typography>
);

const MarketStatusRail: React.FC<MarketStatusRailProps> = ({
  autoTradeEnabled,
  speechAlertEnabled,
  telegramNotificationEnabled,
}) => {
  const { C } = useAppTheme();
  const conn = useConnectionStatus();

  // Compute market open/closed inline. Mirrors the logic that used to live
  // in the old Dashboard header so behaviour is unchanged.
  const now = new Date();
  const day = now.getDay();
  const minutes = now.getHours() * 60 + now.getMinutes();
  const isWeekday = day >= 1 && day <= 5;
  const nseOpen = isWeekday && minutes >= 555 && minutes <= 930;       // 9:15–15:30
  const cdsOpen = isWeekday && minutes >= 540 && minutes <= 1020;      // 9:00–17:00
  const mcxOpen = isWeekday && minutes >= 540 && minutes <= 1410;      // 9:00–23:30
  const cryptoOpen = true;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
      <SectionLabel text="Markets" />
      <Row label="NSE"  on={nseOpen}    C={C} tooltip={`NSE: ${nseOpen ? 'Open (Mon–Fri 9:15–15:30)' : 'Closed'}`} />
      <Row label="CDS"  on={cdsOpen}    C={C} tooltip={`CDS: ${cdsOpen ? 'Open (Mon–Fri 9:00–17:00)' : 'Closed'}`} />
      <Row label="MCX"  on={mcxOpen}    C={C} tooltip={`MCX: ${mcxOpen ? 'Open (Mon–Fri 9:00–23:30)' : 'Closed'}`} />
      <Row label="CRY"  on={cryptoOpen} C={C} tooltip="Crypto: Open (24×7)" />

      <Box sx={{ height: '1px', bgcolor: 'divider', my: 0.4, mx: 0.25 }} />

      <SectionLabel text="Switches" />
      <Row label="Auto"  on={!!autoTradeEnabled}             C={C} tooltip={`Auto trade: ${autoTradeEnabled ? 'On' : 'Off'}`} />
      <Row label="Spch"  on={!!speechAlertEnabled}           C={C} tooltip={`Speech alerts: ${speechAlertEnabled ? 'On' : 'Off'}`} />
      <Row label="Tlgm"  on={!!telegramNotificationEnabled}  C={C} tooltip={`Telegram notifications: ${telegramNotificationEnabled ? 'On' : 'Off'}`} />

      <Box sx={{ height: '1px', bgcolor: 'divider', my: 0.4, mx: 0.25 }} />

      <SectionLabel text="Connections" />
      <ConnRow label="Zrdh" state={conn.zerodha} C={C} tooltip={`Zerodha: ${conn.zerodha === 'ok' ? 'connected' : conn.zerodha === 'off' ? 'disconnected' : 'unknown'}`} />
      <ConnRow label="Dlta" state={conn.delta}   C={C} tooltip={`Delta: ${conn.delta === 'ok' ? 'connected' : conn.delta === 'off' ? 'disconnected' : 'unknown'}`} />
      <ConnRow label="App"  state={conn.app}     C={C} tooltip={`App / backend: ${conn.app === 'ok' ? 'reachable' : 'unreachable'}`} />
    </Box>
  );
};

export default MarketStatusRail;
